import { useEffect } from 'react'
import { Alert, Col, Form, Input, InputNumber, Modal, Row, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { RegionDistrictFields } from '@/features/geo/RegionDistrictFields'
import {
  LATITUDE_MAX,
  LATITUDE_MIN,
  LONGITUDE_MAX,
  LONGITUDE_MIN,
  coordinateError,
  createSite,
  updateSite,
  type SiteEntry,
} from './sitesApi'

interface SiteFormModalProps {
  open: boolean
  /** `null` = yangi joylashuv */
  site: SiteEntry | null
  onClose: () => void
}

interface FormValues {
  name: string
  region?: string
  district?: string
  address?: string
  latitude?: number | null
  longitude?: number | null
  notes?: string
}

/** Joylashuv qo'shish va tahrirlash. */
export function SiteFormModal({ open, site, onClose }: SiteFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      site
        ? {
            name: site.name,
            region: site.region,
            district: site.district ?? undefined,
            address: site.address ?? '',
            latitude: site.latitude,
            longitude: site.longitude,
            notes: site.notes ?? '',
          }
        : { name: '', region: undefined, district: undefined, address: '', notes: '' },
    )
  }, [open, site, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      // Bo'sh satr `null` ga aylantiriladi: bazada bo'sh satr "qiymat bor"
      // bo'lib saqlanardi va kartochkadagi bo'sh katak "to'ldirilgan" bo'lib
      // ko'rinardi (`NasFormModal` bilan bir xil sabab)
      const draft = {
        name: values.name.trim(),
        region: (values.region ?? '').trim(),
        district: values.district?.trim() || null,
        address: values.address?.trim() || null,
        latitude: values.latitude ?? null,
        longitude: values.longitude ?? null,
        notes: values.notes?.trim() || null,
      }
      return site ? updateSite(site.id, draft) : createSite(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sites.all })
      // Yangi viloyat kiritilgan bo'lishi mumkin — takliflar ro'yxati eskiradi
      void queryClient.invalidateQueries({ queryKey: queryKeys.sites.regions })
      form.resetFields()
      onClose()
    },
  })

  /**
   * Koordinatalar IKKALASI BIRGA tekshiriladi — shuning uchun tekshiruv
   * maydonlarning o'zida emas, yuborishdan oldin bajariladi. antd'ning
   * maydon-bo'yicha qoidasi "ikkinchisi ham to'ldirilganmi" ni ko'ra olmasdi.
   */
  // ⚠️ `await form.validateFields()` YOZILMAYDI: forma yaroqsiz bo'lsa u
  //    rad javob beradi va `onOk={() => void submit()}` uni HECH QAYERDA
  //    ushlamasdi — N6 dagi ushlanmagan `pageerror` ning aynan shu ikkinchi
  //    ko'rinishi (`TenantFormModal` da ham shunday edi).
  const submit = () =>
    submitOnValid(form, (values) => {
      const coordinateIssue = coordinateError(values.latitude, values.longitude)
      if (coordinateIssue) {
        form.setFields([
          {
            name: coordinateIssue === 'lonRange' ? 'longitude' : 'latitude',
            errors: [t(`sites.validation.${coordinateIssue}`)],
          },
        ])
        return
      }
      save.mutate(values)
    })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={site ? t('sites.editTitle') : t('sites.createTitle')}
      onCancel={close}
      onOk={() => void submit()}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={620}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('sites.formNote')}
        />

        <Form.Item
          name="name"
          label={t('sites.name')}
          extra={t('sites.nameHint')}
          rules={[{ required: true, message: t('sites.validation.nameRequired') }]}
        >
          <Input autoFocus placeholder={t('sites.namePlaceholder')} />
        </Form.Item>

        {/*
          ⚠️ VILOYAT/TUMAN — FAQAT TANLANADI (GEO §6). Ilgari bu yerda erkin
          `AutoComplete` turardi va "Toshkent"/"toshkent"/"Toshkent " uch
          alohida guruh bo'lib chiqardi: hisobot jimgina bo'linardi, xato
          esa hech qayerda ko'rinmasdi.
        */}
        <RegionDistrictFields regionRequired />

        <Form.Item name="address" label={t('sites.address')}>
          <Input placeholder={t('common.noValue')} />
        </Form.Item>

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('sites.coordinatesHint')}
        </Typography.Text>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col xs={24} sm={12}>
            <Form.Item name="latitude" label={t('sites.latitude')}>
              <InputNumber
                min={LATITUDE_MIN}
                max={LATITUDE_MAX}
                step={0.00001}
                style={{ width: '100%' }}
                placeholder="34.52813"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="longitude" label={t('sites.longitude')}>
              <InputNumber
                min={LONGITUDE_MIN}
                max={LONGITUDE_MAX}
                step={0.00001}
                style={{ width: '100%' }}
                placeholder="69.17233"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label={t('sites.notes')}>
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
